import React, { useState, ReactElement, MouseEvent } from 'react';
import Image from 'next/image';
import striptags from 'striptags';

import { PATHS } from '../../../../constants/paths';
import { Supplier } from '../../../../types';
import { metersToMiles } from '../../../../modules/location';
import { optimizeImageUrl } from '../../../../modules/imageLoader';
import { plaintextSupplierDescriptions } from '../../../../../config';

import { SupplierCardContainer } from './SupplierV2.styled';
import { SupplierCard } from '../../../../components/SupplierCardV2.ts/SupplierCard';
import { MultilineText } from '../../../../components/MultilineText';
import { EnquiryModal } from '../EnquiryModal';

interface SupplierV2Props {
  supplierData: Supplier;
  location: string;
}

const DEFAULT_SUPPLIER_IMAGE =
  'https://s3.eu-west-2.amazonaws.com/assets.feast-it.com/default_supplier.png';

export function SupplierV2({
  supplierData,
  location,
}: SupplierV2Props): ReactElement {
  const [showEnquiryModal, setShowEnquiryModal] = useState(false);

  const categoryTier1 = supplierData.categoryTier1 || '';

  const supplier = {
    uuid: supplierData?.uuid || '',
    name: supplierData?.name || 'Unknown Supplier',
    slug: supplierData?.slug || '',
    images: supplierData?.public?.images || [],
    description: supplierData?.public?.description || '',
    rating:
      supplierData?.average_rating ??
      supplierData?.external?.rating?.overall ??
      0,
    reviews: supplierData?.external?.numReviews || 0,
    distance: supplierData?.distance || 0,
    state: supplierData?.state || '',
    basePrice:
      categoryTier1 === 'food' || categoryTier1 === 'drink'
        ? (supplierData as any)?.base_price
        : undefined,
    registeredCity:
      (supplierData?.public as any)?.logistics?.registeredCity || '',
    responseRate: (supplierData as any)?.overall_message_response_rate || 0,
  };

  const href = PATHS.supplierProfile(supplier.slug);

  const category = location
    ? `${metersToMiles(supplier.distance)} miles from ${location}`
    : supplier.registeredCity;

  const icon = location || supplier.registeredCity ? 'Location' : null;
  const description = supplier.description;

  const handleSupplierClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (supplier.state === 'offline') {
      e.preventDefault();
      setShowEnquiryModal(true);
    }
  };

  const formatRating = (rating: number): string => Number(rating).toFixed(1);

  const getImageSrc = (): string => {
    const imageUrl = supplier?.images?.[0]?.url;
    const rawImageUrl = imageUrl
      ? decodeURIComponent(imageUrl)
      : DEFAULT_SUPPLIER_IMAGE;

    return optimizeImageUrl({
      src: rawImageUrl,
      width: 365,
      quality: 75,
    });
  };

  const renderDescription = () => {
    if (!description) return null;

    const processedDescription = striptags(description);

    return plaintextSupplierDescriptions ? (
      <MultilineText text={processedDescription} />
    ) : (
      <span
        dangerouslySetInnerHTML={{
          __html: processedDescription,
        }}
      />
    );
  };

  return (
    <SupplierCardContainer>
      <SupplierCard
        name={supplier.name}
        uuid={supplier.uuid}
        headingAs="h3"
        category={category}
        images={
          <Image
            src={getImageSrc()}
            alt={supplier.name}
            fill
            sizes="(min-width: 1200px) 365px, (min-width: 768px) 50vw, 100vw"
          />
        }
        description={renderDescription()}
        onClickLink={handleSupplierClick}
        rating={
          Number(supplier.rating) > 0 ? formatRating(supplier.rating) : null
        }
        numOfReviews={supplier.reviews}
        basePrice={supplier.basePrice}
        icon={icon}
        href={href}
        respondsQuickly={supplier.responseRate > 80}
      />

      <EnquiryModal
        isOpen={showEnquiryModal}
        onClose={() => setShowEnquiryModal(false)}
      />
    </SupplierCardContainer>
  );
}
